import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material"
import { skipToken } from "@reduxjs/toolkit/query"
import {
  type ServiceAccount,
  type ServiceAccountApiKey,
  useDeleteServiceAccountApiKeyMutation,
  useGetServiceAccountApiKeysQuery,
} from "../../services/serviceAccountsApi"
import {
  formatDateTime,
  getApiKeyStatus,
  getDeleteApiKeyMutationCacheKey,
  getErrorMessage,
} from "./utils"

type ServiceAccountApiKeysTableProps = {
  activeOrgId: string | null
  isOrgServiceAdmin: boolean
  onApiKeyActionStarted: () => void
  onApiKeyDeleted: (message: string) => void
  onApiKeyError: (message: string) => void
  serviceAccount: ServiceAccount
}

export function ServiceAccountApiKeysTable({
  activeOrgId,
  isOrgServiceAdmin,
  onApiKeyActionStarted,
  onApiKeyDeleted,
  onApiKeyError,
  serviceAccount,
}: ServiceAccountApiKeysTableProps) {
  const deleteMutationCacheKey = getDeleteApiKeyMutationCacheKey(
    serviceAccount.service_account_id
  )
  const {
    currentData: apiKeysResponse,
    isFetching: isApiKeysFetching,
    isLoading: isApiKeysLoading,
  } = useGetServiceAccountApiKeysQuery(
    activeOrgId
      ? {
          service_account: serviceAccount.service_account_id,
        }
      : skipToken
  )
  const apiKeys = apiKeysResponse?.data ?? []
  const [deleteServiceAccountApiKey, { isLoading: isDeletePending }] =
    useDeleteServiceAccountApiKeyMutation({
      fixedCacheKey: deleteMutationCacheKey,
    })
  const manageServiceAccountsTooltip = isOrgServiceAdmin
    ? ""
    : "Requires the organization service admin role."

  const handleDeleteApiKey = async (apiKey: ServiceAccountApiKey) => {
    if (!isOrgServiceAdmin) {
      onApiKeyError(
        "You need the organization service admin role to delete API keys."
      )
      return
    }

    if (!activeOrgId || !apiKey.key_id) {
      onApiKeyError("No API key is available to delete.")
      return
    }

    const confirmed = window.confirm(
      `Delete the API key ending in ${apiKey.last_four} for "${serviceAccount.label}"?`
    )

    if (!confirmed) {
      return
    }

    onApiKeyActionStarted()

    try {
      await deleteServiceAccountApiKey({
        key_id: apiKey.key_id,
        org_id: activeOrgId,
        service_account: serviceAccount.service_account_id,
      }).unwrap()

      onApiKeyDeleted(
        `Deleted the API key ending in ${apiKey.last_four} for "${serviceAccount.label}".`
      )
    } catch (error) {
      onApiKeyError(getErrorMessage(error) ?? "Unable to delete the API key.")
    }
  }

  if (isApiKeysLoading) {
    return <Skeleton variant="rectangular" width="100%" height={118} />
  }
  return (
    <TableContainer
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Status</TableCell>
            <TableCell>Expiration</TableCell>
            <TableCell>Last Four</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {apiKeys.length > 0 ? (
            apiKeys.map((apiKey) => {
              const apiKeyStatus = getApiKeyStatus(apiKey)

              return (
                <TableRow
                  key={
                    apiKey.key_id ?? `${apiKey.created_at}-${apiKey.last_four}`
                  }
                >
                  <TableCell>
                    <Chip
                      color={apiKeyStatus.color}
                      label={apiKeyStatus.label}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDateTime(apiKey.expires_at)}</TableCell>
                  <TableCell>
                    {apiKey.last_four ? (
                      <Box component="code">{apiKey.last_four}</Box>
                    ) : (
                      "Unavailable"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={manageServiceAccountsTooltip}>
                      <span>
                        <Button
                          color="error"
                          disabled={
                            isDeletePending ||
                            isApiKeysFetching ||
                            !isOrgServiceAdmin
                          }
                          onClick={() => {
                            void handleDeleteApiKey(apiKey)
                          }}
                          size="small"
                        >
                          {isDeletePending ? "Deleting..." : "Delete"}
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell
                align="center"
                colSpan={4}
                sx={{ color: "textSecondary" }}
              >
                No API keys for this service account.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
